// Вибраний навчальний рік / семестр (топбар): дані — GET /api/directories/semesters/items,
// стан — у контексті й URL (?semester=<код семестру>, напр. 2026-2027-1), останній вибір — у localStorage.
// Типово — поточний семестр сервера (GET /api/contingent/semesters/current, U04: pickCurrentSemester за Києвом); доки він
// невідомий або сервер без U04 — клієнтська оцінка (семестр, що містить сьогодні, інакше найближчий майбутній, інакше найновіший).
// Модулі читають useSemester().
import { useQuery } from '@tanstack/react-query';
import { createContext, use, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { api, type Paged } from '@/lib/api';
import { todayIso } from '@/lib/format';
import { queryKeys } from '@/lib/query';

export const SEMESTER_PARAM = 'semester';
const STORAGE_KEY = 'unisched-semester';

/** Семестр зі списку довідника semesters (поля — з реєстру; academicYear — якщо API вкладає зв'язок). */
export interface SemesterItem {
  id: string;
  code: string;
  name?: string | null;
  number: number;
  startDate: string;
  endDate: string;
  academicYearId: string;
  weeks?: number | null;
  academicYear?: { id: string; code: string; name?: string | null } | null;
  /** Посилання запису довідника (рушій U01): academicYearId → { id, code, label }. */
  _refs?: Record<string, unknown>;
}

export interface SemesterContextValue {
  semesters: SemesterItem[];
  isLoading: boolean;
  error: unknown;
  semester: SemesterItem | null;
  semesterId: string | null;
  /** Код навчального року вибраного семестру (напр. 2026-2027). */
  academicYearCode: string | null;
  setSemester: (idOrCode: string) => void;
  /** Рядок запиту для посилань, що зберігають вибір (напр. '?semester=2026-2027-1'). */
  search: string;
}

const SemesterContext = createContext<SemesterContextValue | null>(null);

/** Код навчального року семестру: _refs довідника, вкладений зв'язок або префікс коду «РРРР-РРРР-N»; невідомо — null. */
export function academicYearCodeOf(s: SemesterItem): string | null {
  const ref = s._refs?.academicYearId as { code?: unknown } | null | undefined;
  // рушій повертає { code: id }, якщо посилання не знайдено — це не код
  if (ref && typeof ref.code === 'string' && ref.code && ref.code !== s.academicYearId) return ref.code;
  if (s.academicYear?.code) return s.academicYear.code;
  const m = /^(\d{4}-\d{4})/.exec(s.code);
  return m ? m[1] : null;
}

/** Підпис семестру: назва з довідника або «1 семестр». */
export function semesterLabel(s: SemesterItem): string {
  return s.name?.trim() || `${s.number} семестр`;
}

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function store(code: string) {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* ігноруємо */
  }
}

export function SemesterProvider({ children }: { children: ReactNode }) {
  const [sp, setSp] = useSearchParams();
  const q = useQuery({
    queryKey: queryKeys.semesters,
    queryFn: () => api.get<Paged<SemesterItem>>('/directories/semesters/items', { pageSize: 200, sort: 'startDate:desc' }),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const semesters = useMemo(
    () => [...(q.data?.items ?? [])].sort((a, b) => (a.startDate < b.startDate ? 1 : a.startDate > b.startDate ? -1 : 0)),
    [q.data],
  );

  // Поточний семестр сервера (той самий ключ кешу, що й у U04 — features/contingent/api.ts).
  const current = useQuery({
    queryKey: ['contingent', 'semesters', 'current'] as const,
    queryFn: async () => (await api.get<{ semester: { id: string } } | null | undefined>('/contingent/semesters/current')) ?? null,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const fromUrl = sp.get(SEMESTER_PARAM);
  const semester = useMemo<SemesterItem | null>(() => {
    if (!semesters.length) return null;
    const find = (key: string | null) => (key ? (semesters.find((s) => s.code === key || s.id === key) ?? null) : null);
    const explicit = find(fromUrl) ?? find(readStored());
    if (explicit) return explicit;
    // Без явного вибору чекаємо відповіді сервера (інакше URL зафіксує клієнтську оцінку раніше за неї).
    if (current.isPending) return null;
    const server = find(current.data?.semester.id ?? null);
    if (server) return server;
    const today = todayIso();
    const future = semesters.filter((s) => s.startDate > today);
    return (
      semesters.find((s) => s.startDate <= today && today <= s.endDate) ??
      (future.length ? future[future.length - 1] : null) ??
      semesters[0]
    );
  }, [semesters, fromUrl, current.isPending, current.data]);

  // Вибір — завжди в URL (посилання можна переслати).
  useEffect(() => {
    if (semester && fromUrl !== semester.code) {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(SEMESTER_PARAM, semester.code);
          return next;
        },
        { replace: true },
      );
    }
  }, [semester, fromUrl, setSp]);

  const setSemester = useCallback(
    (idOrCode: string) => {
      const s = semesters.find((x) => x.id === idOrCode || x.code === idOrCode);
      if (!s) return;
      store(s.code);
      setSp((prev) => {
        const next = new URLSearchParams(prev);
        next.set(SEMESTER_PARAM, s.code);
        next.delete('page');
        return next;
      });
    },
    [semesters, setSp],
  );

  const value = useMemo<SemesterContextValue>(
    () => ({
      semesters,
      isLoading: q.isLoading || (!semester && semesters.length > 0 && current.isPending),
      error: q.error,
      semester,
      semesterId: semester?.id ?? null,
      academicYearCode: semester ? academicYearCodeOf(semester) : null,
      setSemester,
      search: semester ? `?${SEMESTER_PARAM}=${encodeURIComponent(semester.code)}` : '',
    }),
    [semesters, q.isLoading, q.error, semester, setSemester, current.isPending],
  );

  return <SemesterContext value={value}>{children}</SemesterContext>;
}

export function useSemester(): SemesterContextValue {
  const ctx = use(SemesterContext);
  if (!ctx) throw new Error('useSemester() поза <SemesterProvider>');
  return ctx;
}
